<script setup lang="ts">
import { computed, ref } from 'vue'

import draggable from 'vuedraggable'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import SettingImageSelector from '@/components/beta-editor/settings/controls/BetaSettingImageSelector.vue'
import SettingLink from '@/components/atoms/SettingLink.vue'
import SettingText from '@/components/atoms/SettingText.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import modal from '@/services/modal'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import { getGalleryPresetFamily } from '@/utils/gallery-presets'

const BETA_GALLERY_MEDIA_SELECTION_LIMIT = Number.MAX_SAFE_INTEGER

const props = defineProps<{
    element: any
    extra: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    isDarkMode: boolean
    isFull: boolean
    isNew?: boolean
    isNewBefore?: boolean
    isChild?: boolean
    elementParent?: any
    elementParentIndex?: number
    isEditable?: boolean
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()
const mediaManagerStore = useMediaManagerStore()
const openModal = ref(false)
const itemForEdit = ref<any>(null)

const content = computed(() => props.element.frameContent ?? { items: [] })
const galleryItems = computed(() => Array.isArray(content.value?.items) ? content.value.items : [])
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const previewDevice = computed(() => betaStore.previewDevice)
const isHomeSliderGallery = computed(() => editorPageStore.isProtectedHomeFirstGallery(props.element))
const galleryDesignDefaults = computed(() => (defaultStore.newSection.extra ?? {}) as Record<string, any>)
const galleryPresetOptions = computed(() => layoutStore.templateOrganismsCombinations?.maincontent ?? [])
const savedGalleryPresetVariables = computed(() => {
    const presetId = Number(props.extra?.maincontent_gallery_id ?? 0)

    if (!Number.isFinite(presetId) || presetId <= 0) return null

    const matchedPreset = galleryPresetOptions.value.find((option: any) => Number(option.id) === presetId)

    return matchedPreset?.variables ?? null
})
const isUsingDesignDefaultGallery = computed(() => {
    const configuredType = props.extra?.maincontent_gallery

    return !configuredType || configuredType === 'gallery-default'
})

const getExplicitGalleryExtraValue = (key: string) => {
    const explicitValue = props.extra?.[key]

    if (explicitValue === undefined || explicitValue === null || explicitValue === '') return undefined

    return explicitValue
}

const getResolvedGalleryExtraValue = (key: string, fallback?: any) => {
    const explicitValue = getExplicitGalleryExtraValue(key)

    if (isUsingDesignDefaultGallery.value) {
        return explicitValue ?? galleryDesignDefaults.value?.[key] ?? fallback
    }

    return explicitValue ?? savedGalleryPresetVariables.value?.[key] ?? galleryDesignDefaults.value?.[key] ?? fallback
}

const galleryType = computed(() => {
    const configuredType = getResolvedGalleryExtraValue('maincontent_gallery', props.element?.component || 'gallery-isotope')

    return String(configuredType || 'gallery-isotope').toLowerCase()
})
const galleryItemsPerRow = computed(() => {
    const rawValue = Number(getResolvedGalleryExtraValue('maincontent_gallery_items', 4))

    if (!Number.isFinite(rawValue) || rawValue <= 0) return 4

    return Math.min(Math.max(rawValue, 1), 6)
})
const galleryItemsPerRowMobile = computed(() => {
    const rawValue = Number(getResolvedGalleryExtraValue('maincontent_gallery_items_mobile', galleryItemsPerRow.value))

    if (!Number.isFinite(rawValue) || rawValue <= 0) return Math.min(galleryItemsPerRow.value, 2)

    return Math.min(Math.max(rawValue, 1), 6)
})
const galleryGap = computed(() => {
    const rawValue = Number(getResolvedGalleryExtraValue('maincontent_gallery_padding', 0))

    if (!Number.isFinite(rawValue) || rawValue < 0) return 0

    return rawValue * 8
})
const galleryCropEnabled = computed(() => String(getResolvedGalleryExtraValue('maincontent_gallery_items_crop', '0')) === '1')
const galleryPreviewFamily = computed(() => getGalleryPresetFamily(galleryType.value))
const effectiveGalleryItemsPerRow = computed(() => {
    if (galleryPreviewFamily.value === 'carousel' && !galleryCropEnabled.value) return 1

    return galleryItemsPerRow.value
})
const galleryPreviewMode = computed(() => {
    if (galleryPreviewFamily.value === 'imageflex') return 'flex'

    return galleryPreviewFamily.value
})
const galleryRenderMode = computed(() => (isHomeSliderGallery.value ? 'home-slider' : galleryPreviewMode.value))
const galleryContainerStyle = computed(() => {
    if (isHomeSliderGallery.value) {
        return {
            '--preview-gallery-columns': '4',
            '--preview-gallery-tablet-columns': '3',
            '--preview-gallery-mobile-columns': '2',
            '--preview-gallery-gap': '16px',
            '--preview-gallery-gap-half': '8px',
        }
    }

    return {
        '--preview-gallery-columns': String(Math.min(effectiveGalleryItemsPerRow.value, 6)),
        '--preview-gallery-visible-items': String(Math.min(Math.max(effectiveGalleryItemsPerRow.value, 1), 4)),
        '--preview-gallery-tablet-columns': String(Math.min(effectiveGalleryItemsPerRow.value, 2)),
        '--preview-gallery-tablet-visible-items': String(Math.min(Math.max(effectiveGalleryItemsPerRow.value, 1), 2)),
        '--preview-gallery-mobile-columns': String(Math.min(galleryItemsPerRowMobile.value, 4)),
        '--preview-gallery-mobile-visible-items': String(Math.min(Math.max(galleryItemsPerRowMobile.value, 1), 2)),
        '--preview-gallery-gap': `${galleryGap.value}px`,
        '--preview-gallery-gap-half': `${galleryGap.value / 2}px`,
    }
})
const showGalleryText = computed(() => String(content.value?.showText ?? '0') === '1' && Boolean(content.value?.text))

const normalizeMediaSource = (value: any) => (typeof value === 'string' ? value.trim() : '')
const isExternalImageSource = (value: any) => {
    const source = normalizeMediaSource(value)

    return (
        source.startsWith('http://')
        || source.startsWith('https://')
        || source.startsWith('//')
        || source.startsWith('data:')
        || source.startsWith('blob:')
    )
}
const getImage = (item: any) => {
    const image = item?.image
    const candidates = [
        typeof image === 'string' ? image : '',
        image?.url,
        image?.uri,
        image?.content,
        image?.src,
        item?.thumbnail,
        item?.poster,
        item?.cover,
        item?.url,
        item?.content,
        item?.src,
        item?.mainImage,
        item?.embedFile,
    ].map(normalizeMediaSource)

    return candidates.find(source => source && !source.startsWith('<')) ?? ''
}
const getFlexRowHeight = (itemsPerRow: number) => {
    if (itemsPerRow === 1) return 360
    if (itemsPerRow === 2) return 280
    if (itemsPerRow === 3) return 220
    if (itemsPerRow === 4) return 170
    if (itemsPerRow === 5) return 145
    if (itemsPerRow === 6) return 120

    return 200
}
const getFlexRowByIndex = (index: number) => {
    const rowSize = Math.max(effectiveGalleryItemsPerRow.value, 1)
    const rowStart = Math.floor(index / rowSize) * rowSize

    return galleryItems.value.slice(rowStart, rowStart + rowSize)
}
const getGalleryItemAspectRatio = (item: any, fallback = '4 / 3') => {
    const width = Number(item?.image?.width ?? 0)
    const height = Number(item?.image?.height ?? 0)

    if (width > 0 && height > 0) return `${width} / ${height}`

    return fallback
}
const getGalleryMediaStyle = (item: any, index: number) => {
    if (isHomeSliderGallery.value) {
        return {
            aspectRatio: '16 / 10',
        }
    }

    if (galleryPreviewFamily.value === 'carousel') {
        return {
            aspectRatio: galleryCropEnabled.value ? '16 / 10' : getGalleryItemAspectRatio(item, '16 / 10'),
        }
    }

    if (galleryPreviewMode.value === 'flex') {
        return {
            height: `${getFlexRowHeight(effectiveGalleryItemsPerRow.value)}px`,
        }
    }

    return {
        aspectRatio: galleryCropEnabled.value ? '1 / 1' : getGalleryItemAspectRatio(item),
    }
}
const getGalleryItemStyle = (item: any, index: number) => {
    if (isHomeSliderGallery.value) return undefined
    if (galleryPreviewMode.value !== 'flex') return undefined

    const row = getFlexRowByIndex(index)

    if (row.length === 0) {
        return {
            width: '100%',
            flex: '0 0 100%',
        }
    }

    if (galleryCropEnabled.value) {
        const fraction = 1 / row.length
        const gapTotal = galleryGap.value * Math.max(row.length - 1, 0)
        const widthExpression = `calc((100% - ${gapTotal}px) * ${fraction})`

        return {
            width: widthExpression,
            flex: `0 0 ${widthExpression}`,
        }
    }

    const ratios = row.map((currentItem: any) => {
        const width = Number(currentItem?.image?.width ?? 0)
        const height = Number(currentItem?.image?.height ?? 0)

        if (width > 0 && height > 0) return width / height

        return 4 / 3
    })
    const totalRatio = ratios.reduce((accumulator: number, value: number) => accumulator + value, 0) || row.length
    const itemIndexInRow = row.indexOf(item)
    const itemRatio = ratios[itemIndexInRow] || (1 / row.length)
    const fraction = itemRatio / totalRatio
    const gapTotal = galleryGap.value * Math.max(row.length - 1, 0)
    const widthExpression = `calc((100% - ${gapTotal}px) * ${fraction})`

    return {
        width: widthExpression,
        flex: `0 0 ${widthExpression}`,
    }
}
const getGalleryFocalPoint = (item: any) => item?.focalPoint ?? { x: 50, y: 50 }
const hasCarouselContent = (item: any) => {
    if (isHomeSliderGallery.value) return false
    if (galleryPreviewFamily.value !== 'carousel') return false

    return Boolean(item?.title || item?.subtitle || (item?.button && item?.buttonText))
}

const updateGalleryItems = (items: any[]) => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: {
            items,
        },
    })
}
const onReorder = () => {
    updateGalleryItems([...(content.value?.items ?? [])])
}
const onEditGalleryItem = (item: any, index: number) => {
    itemForEdit.value = {
        ...item,
        index,
    }
    openModal.value = true
}
const onAddMediaInGallery = () => {
    mediaManagerStore.setMaxSelectedMedias(BETA_GALLERY_MEDIA_SELECTION_LIMIT)
    mediaManagerStore.setIsOpenModal(true)
    mediaManagerStore.setAction((medias: any[]) => {
        const newItems = medias.map((media: any) => ({
            ...defaultStore.newElementGalleryItem,
            type: media.type,
            image: {
                url: media.content,
                width: media.width,
                height: media.height,
                alt: media.name,
                createdAt: media.datetime,
            },
            embed: media.embed ?? null,
        }))

        updateGalleryItems([...galleryItems.value, ...newItems])
    })
}
const onChangeGalleryItem = (index: number) => {
    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setIsOpenModal(true)
    mediaManagerStore.setAction((media: any) => {
        if (!media) return

        const selectedMedia = Array.isArray(media) ? media[0] : media
        const nextItems = [...galleryItems.value]

        nextItems[index] = {
            ...nextItems[index],
            type: selectedMedia.type,
            image: {
                ...(nextItems[index]?.image ?? {}),
                url: selectedMedia.content,
                width: selectedMedia.width,
                height: selectedMedia.height,
                alt: selectedMedia.name,
                createdAt: selectedMedia.datetime,
            },
            embed: selectedMedia.embed ?? null,
        }

        updateGalleryItems(nextItems)
    })
}
const onRemoveGalleryItem = async (index: number) => {
    const result = await modal.confirmation('Confirmar Exclusão', 'Deseja realmente excluir este item?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    const nextItems = [...galleryItems.value]
    nextItems.splice(index, 1)
    updateGalleryItems(nextItems)
}
const handleEditFocalPointGalleryItem = (event: any) => {
    if (!itemForEdit.value) return

    itemForEdit.value.focalPoint = {
        x: event.value.x,
        y: event.value.y,
    }
}
const handleEditTitleGalleryItem = (event: any) => {
    if (!itemForEdit.value) return

    itemForEdit.value.title = event.value
}
const handleEditSubtitleGalleryItem = (event: any) => {
    if (!itemForEdit.value) return

    itemForEdit.value.subtitle = event.value
}
const handleEditLinkGalleryItem = (event: any) => {
    if (!itemForEdit.value) return

    if (event.target === 'link') itemForEdit.value.href = event.value
    if (event.target === 'open-in') itemForEdit.value.target = event.value
}
const handleEditVideoMobile = (event: any) => {
    if (!itemForEdit.value) return

    itemForEdit.value.embedMobile = event.value
}
const handleEditButtonText = (event: any) => {
    if (!itemForEdit.value) return

    itemForEdit.value.buttonText = event.value
}
const handleSaveGalleryItem = () => {
    if (!itemForEdit.value) return

    const nextItems = [...galleryItems.value]
    const { index, ...item } = itemForEdit.value

    nextItems[index] = item
    updateGalleryItems(nextItems)
    openModal.value = false
}
</script>

<template>
    <div
        class="editor-gallery-beta"
        :data-active="isActive"
        :data-home-slider-gallery="isHomeSliderGallery"
        :data-preview-device="previewDevice"
        :schema="JSON.stringify(schema)"
    >
        <base-modal
            :open-watch="openModal"
            :title="'Editar Mídia'"
            size="custom"
            width="w-[40vw]"
            height="h-[90vh]"
            rounded="2xl"
            @modal-closed="() => openModal = false"
        >
            <template #body>
                <div v-if="itemForEdit" class="flex h-full flex-col">
                    <div class="flex-1 space-y-3">
                        <setting-image-selector
                            v-if="itemForEdit.image"
                            label="Selecione o ponto focal das imagens para mobile"
                            :value="itemForEdit.image.url"
                            :focal-point="itemForEdit.focalPoint"
                            :border="false"
                            :with-change="false"
                            :with-focal-point="true"
                            class-image="w-1/3"
                            @change="handleEditFocalPointGalleryItem"
                        />

                        <setting-text
                            label="Título"
                            placeholder="'Ex: Mídia 1'"
                            :value="itemForEdit.title"
                            @change="handleEditTitleGalleryItem"
                        />

                        <setting-text
                            label="Descrição"
                            placeholder="'Ex: Descrição da Mídia 1'"
                            :value="itemForEdit.subtitle"
                            @change="handleEditSubtitleGalleryItem"
                        />

                        <setting-link
                            label="Link"
                            type="string"
                            :value="itemForEdit.href"
                            :value-open-in="itemForEdit.target"
                            @change="handleEditLinkGalleryItem"
                        />

                        <settings-layout name="Configurações de botões">
                            <div class="flex flex-col space-y-3">
                                <label for="button" class="relative inline-flex cursor-pointer items-center">
                                    <input
                                        id="button"
                                        v-model="itemForEdit.button"
                                        type="checkbox"
                                        name="button"
                                        class="peer sr-only"
                                    />

                                    <div
                                        class="h-6 w-11 rounded-full bg-gray-200 outline-none after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[var(--main-color)] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-700 dark:border-gray-600"
                                    />

                                    <span class="ml-3 text-sm font-medium text-gray-900">
                                        Exibir botão
                                    </span>
                                </label>

                                <div v-if="itemForEdit.button">
                                    <setting-text
                                        label="Texto do botão"
                                        placeholder="'Ex: Saiba mais'"
                                        :value="itemForEdit.buttonText"
                                        @change="handleEditButtonText"
                                    />
                                </div>
                            </div>
                        </settings-layout>

                        <settings-layout
                            v-if="itemForEdit.type === 'video'"
                            name="Configurações avançadas de vídeo"
                        >
                            <div class="flex flex-col space-y-3">
                                <label for="autoplay" class="relative inline-flex cursor-pointer items-center">
                                    <input
                                        id="autoplay"
                                        v-model="itemForEdit.autoplay"
                                        type="checkbox"
                                        name="autoplay"
                                        class="peer sr-only"
                                    />

                                    <div
                                        class="h-6 w-11 rounded-full bg-gray-200 outline-none after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[var(--main-color)] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-700 dark:border-gray-600"
                                    />

                                    <span class="ml-3 text-sm font-medium text-gray-900">
                                        Reproduzir automaticamente. (mudo)
                                    </span>
                                </label>

                                <label for="expand" class="relative inline-flex cursor-pointer items-center">
                                    <input
                                        id="expand"
                                        v-model="itemForEdit.expand"
                                        type="checkbox"
                                        name="expand"
                                        class="peer sr-only"
                                    />

                                    <div
                                        class="h-6 w-11 rounded-full bg-gray-200 outline-none after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[var(--main-color)] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-700 dark:border-gray-600"
                                    />

                                    <span class="ml-3 text-sm font-medium text-gray-900">
                                        Ampliar ao clicar. (com áudio)
                                    </span>
                                </label>

                                <setting-text
                                    label="Video Mobile"
                                    placeholder="'Link do vídeo mobile'"
                                    :value="itemForEdit.embedMobile"
                                    @change="handleEditVideoMobile"
                                />
                            </div>
                        </settings-layout>
                    </div>

                    <div class="mt-4 flex justify-end space-x-2">
                        <button
                            class="rounded-full bg-transparent px-8 py-3 text-base font-bold text-[#060606] opacity-30 outline-none hover:text-gray-500"
                            @click="() => openModal = false"
                        >
                            Cancelar
                        </button>

                        <button
                            class="rounded-full bg-[var(--main-color)] px-8 py-3 text-base font-bold text-white outline-none hover:bg-[#4D4D4D]"
                            @click="handleSaveGalleryItem"
                        >
                            Salvar alterações
                        </button>
                    </div>
                </div>
            </template>
        </base-modal>

        <div v-if="showGalleryText" class="editor-gallery-beta__heading" v-html="content.text" />

        <div v-if="galleryItems.length === 0" class="editor-gallery-beta__empty">
            <button class="editor-gallery-beta__add" @click.stop="onAddMediaInGallery">
                Adicionar Mídia
            </button>
        </div>

        <draggable
            v-else
            :list="content.items"
            handle=".action-move-item-gallery"
            item-key="index"
            :class="[
                'editor-gallery-beta__items',
                `editor-gallery-beta__items--${galleryRenderMode}`,
            ]"
            :style="galleryContainerStyle"
            @change="onReorder"
        >
            <template #item="{ element: item, index }">
                <article class="editor-gallery-beta__item" :style="getGalleryItemStyle(item, index)">
                    <div class="editor-gallery-beta__media" :style="getGalleryMediaStyle(item, index)">
                        <template v-if="isActive">
                            <button class="action-move-item-gallery z-20 absolute top-3 left-4" @click.stop>
                                <span class="sr-only">Mover mídia</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3.84219 10.2422L1.6001 8.0001M1.6001 8.0001L3.84219 5.758M1.6001 8.0001H14.4001M12.158 10.2422L14.4001 8.0001M14.4001 8.0001L12.158 5.758M5.758 3.84219L8.0001 1.6001M8.0001 1.6001L10.2422 3.84219M8.0001 1.6001L8.0001 14.4001M5.758 12.158L8.0001 14.4001M8.0001 14.4001L10.2422 12.158" stroke="#FAFAFA" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>

                            <button class="z-20 absolute top-3 right-4" @click.stop="onEditGalleryItem(item, index)">
                                <span class="sr-only">Editar mídia</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.98101 3.50714C10.3713 3.11659 11.0044 3.11633 11.395 3.50655L12.4923 4.60258C12.8831 4.99297 12.8834 5.62629 12.4929 6.017L6.16124 12.3521C6.02196 12.4915 5.84462 12.5866 5.65147 12.6255L2.80005 13.2L3.37564 10.3519C3.41459 10.1592 3.50951 9.98219 3.64852 9.8431L9.98101 3.50714Z" stroke="#FAFAFA" stroke-width="1.5" stroke-linejoin="round"/>
                                </svg>
                            </button>

                            <div class="z-20 absolute bottom-3 right-4 space-x-3">
                                <button @click.stop="onChangeGalleryItem(index)">
                                    <span class="sr-only">Trocar mídia</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M5.33342 12.9126C3.73928 11.9892 2.66675 10.2632 2.66675 8.28643C2.66675 6.0451 4.04553 4.12625 6.00008 3.33399M6.00008 11.2905L6.00008 13.9609H3.33342M10.6667 3.71502C12.2609 4.63844 13.3334 6.36439 13.3334 8.34119C13.3334 10.5825 11.9546 12.5014 10.0001 13.2936M10.0001 5.33707V2.66675L12.6667 2.66675" stroke="#FAFAFA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>

                                <button @click.stop="onRemoveGalleryItem(index)">
                                    <span class="sr-only">Remover mídia</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2.66675 4.11765H13.3334M6.00008 2H10.0001M10.3334 14H5.66675C4.93037 14 4.33341 13.3679 4.33341 12.5882L4.02902 4.85292C4.01323 4.45189 4.31603 4.11765 4.6951 4.11765H11.3051C11.6841 4.11765 11.9869 4.45189 11.9711 4.85292L11.6667 12.5882C11.6667 13.3679 11.0698 14 10.3334 14Z" stroke="#FAFAFA" stroke-width="1.5" stroke-linecap="round"/>
                                    </svg>
                                </button>
                            </div>

                            <div class="absolute inset-0 z-10 bg-black opacity-30" />
                        </template>

                        <svg
                            v-if="item.type === 'video'"
                            class="editor-gallery-beta__play"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                        </svg>

                        <beta-base-image
                            v-if="getImage(item)"
                            class-name="editor-gallery-beta__image"
                            :src="getImage(item)"
                            :alt="item.image?.alt || item.title || `Midia ${index + 1}`"
                            :external-image="isExternalImageSource(getImage(item))"
                            :style="{
                                objectPosition: `${getGalleryFocalPoint(item).x}% ${getGalleryFocalPoint(item).y}%`,
                            }"
                        />

                        <div v-if="hasCarouselContent(item)" class="editor-gallery-beta__carousel-copy">
                            <p v-if="item.date" class="editor-gallery-beta__carousel-date">
                                {{ item.date }}
                            </p>

                            <h4 v-if="item.title">{{ item.title }}</h4>
                            <p v-if="item.subtitle">{{ item.subtitle }}</p>

                            <span v-if="item.button && item.buttonText" class="editor-gallery-beta__carousel-button">
                                {{ item.buttonText }}
                            </span>
                        </div>

                        <div v-else-if="!getImage(item)" class="editor-gallery-beta__placeholder">
                            Sem mídia
                        </div>
                    </div>
                </article>
            </template>
        </draggable>
    </div>
</template>

<style scoped>
.editor-gallery-beta {
    @apply relative grid w-full gap-4 border-2 border-transparent transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-gallery-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}

.editor-gallery-beta__heading {
    @apply text-[#111827];
}

.editor-gallery-beta__heading :deep(h1),
.editor-gallery-beta__heading :deep(h2),
.editor-gallery-beta__heading :deep(h3),
.editor-gallery-beta__heading :deep(h4) {
    @apply m-0 text-2xl font-bold leading-tight;
}

.editor-gallery-beta__heading :deep(p) {
    @apply mt-2 text-sm text-[#6B7280];
}

.editor-gallery-beta__empty {
    @apply flex min-h-[220px] items-center justify-center rounded-2xl border-2 border-dashed border-[var(--main-color)] bg-white;
}

.editor-gallery-beta__add {
    @apply rounded-full bg-[var(--main-color)] px-5 py-3 text-sm font-bold text-white;
}

.editor-gallery-beta__items {
    width: 100%;
    --preview-gallery-columns: 3;
    --preview-gallery-gap: 0px;
}

.editor-gallery-beta__items--grid,
.editor-gallery-beta__items--isotope {
    display: grid;
    grid-template-columns: repeat(var(--preview-gallery-columns), minmax(0, 1fr));
    gap: var(--preview-gallery-gap);
}

.editor-gallery-beta__items--home-slider {
    display: grid;
    grid-template-columns: repeat(var(--preview-gallery-columns), minmax(0, 1fr));
    gap: var(--preview-gallery-gap);
    border: 1px solid #DCE7F5;
    border-radius: 16px;
    background: #F8FBFF;
    padding: 16px;
}

.editor-gallery-beta__items--isotope {
    gap: 0;
    margin-inline: calc(var(--preview-gallery-gap-half) * -1);
}

.editor-gallery-beta__items--isotope .editor-gallery-beta__item {
    padding: var(--preview-gallery-gap-half);
}

.editor-gallery-beta__items--carousel {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: calc((100% - (var(--preview-gallery-visible-items) - 1) * var(--preview-gallery-gap)) / var(--preview-gallery-visible-items));
    gap: var(--preview-gallery-gap);
    overflow-x: auto;
    padding-bottom: 8px;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.editor-gallery-beta__items--carousel::-webkit-scrollbar {
    display: none;
}

.editor-gallery-beta__items--flex,
.editor-gallery-beta__items--imageflex {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: var(--preview-gallery-gap);
}

.editor-gallery-beta__item {
    @apply relative w-full overflow-hidden rounded-lg;
}

.editor-gallery-beta__items--carousel .editor-gallery-beta__item {
    scroll-snap-align: start;
}

.editor-gallery-beta__media {
    @apply relative w-full overflow-hidden bg-[#ECECEC];
}

.editor-gallery-beta__items--home-slider .editor-gallery-beta__media {
    border-radius: 8px;
}

.editor-gallery-beta__image {
    @apply absolute inset-0 h-full w-full object-cover;
}

.editor-gallery-beta__play {
    @apply absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 fill-white drop-shadow-md;
}

.editor-gallery-beta__placeholder {
    @apply flex h-full min-h-[180px] items-center justify-center text-sm font-semibold text-[#94A3B8];
}

.action-move-item-gallery {
    @apply cursor-move;
}

.editor-gallery-beta__media button {
    @apply outline-none;
}

.editor-gallery-beta__carousel-copy {
    @apply absolute inset-x-0 bottom-0 z-10 flex flex-col gap-2 px-5 py-5 text-white;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.78) 100%);
}

.editor-gallery-beta__carousel-date {
    @apply m-0 text-xs font-medium text-white/80;
}

.editor-gallery-beta__carousel-copy h4 {
    @apply m-0 text-2xl font-bold leading-tight;
}

.editor-gallery-beta__carousel-copy p {
    @apply m-0 text-sm leading-relaxed text-white/90;
}

.editor-gallery-beta__carousel-button {
    @apply mt-2 inline-flex w-fit rounded-full bg-white px-4 py-2 text-xs font-bold text-[#111827];
}

@media (max-width: 1024px) {
    .editor-gallery-beta__items--grid,
    .editor-gallery-beta__items--isotope,
    .editor-gallery-beta__items--home-slider {
        grid-template-columns: repeat(var(--preview-gallery-tablet-columns), minmax(0, 1fr));
    }

    .editor-gallery-beta__items--carousel {
        grid-auto-columns: calc((100% - (var(--preview-gallery-tablet-visible-items) - 1) * var(--preview-gallery-gap)) / var(--preview-gallery-tablet-visible-items));
    }
}

@media (max-width: 768px) {
    .editor-gallery-beta__items--grid,
    .editor-gallery-beta__items--isotope,
    .editor-gallery-beta__items--home-slider {
        grid-template-columns: repeat(var(--preview-gallery-mobile-columns), minmax(0, 1fr));
    }

    .editor-gallery-beta__items--carousel {
        grid-auto-columns: calc((100% - (var(--preview-gallery-mobile-visible-items) - 1) * var(--preview-gallery-gap)) / var(--preview-gallery-mobile-visible-items));
    }
}

.editor-gallery-beta[data-preview-device='mobile'] .editor-gallery-beta__items--grid,
.editor-gallery-beta[data-preview-device='mobile'] .editor-gallery-beta__items--isotope,
.editor-gallery-beta[data-preview-device='mobile'] .editor-gallery-beta__items--home-slider {
    grid-template-columns: repeat(var(--preview-gallery-mobile-columns), minmax(0, 1fr));
}

.editor-gallery-beta[data-preview-device='mobile'] .editor-gallery-beta__items--carousel {
    grid-auto-columns: calc((100% - (var(--preview-gallery-mobile-visible-items) - 1) * var(--preview-gallery-gap)) / var(--preview-gallery-mobile-visible-items));
}
</style>
